"use client"

import type { ElementType } from "react"
import {
  Toaster as ChakraToaster,
  Icon,
  Portal,
  Spinner,
  Stack,
  Text,
  HStack,
  Toast,
  createToaster,
} from "@chakra-ui/react"
import { LuFlame, LuRocket, LuTrophy } from "react-icons/lu"

export const toaster = createToaster({
  placement: "top",
  pauseOnPageIdle: true,
})

const MODE_THEME: Record<
  string,
  { color: string; border: string; icon?: ElementType }
> = {
  scoreUpgrade: {
    color: "orange.500",
    border: "orange.300",
    icon: LuFlame,
  },
  xpUpgrade: {
    color: "cyan.500",
    border: "cyan.300",
    icon: LuTrophy,
  },
  levelUpgrade: {
    color: "green.500",
    border: "green.300",
    icon: LuRocket,
  },
}

export const Toaster = () => {
  return (
    <Portal>
      <ChakraToaster toaster={toaster}>
        {(toast) => {
          const mode = toast.meta?.mode as string | undefined
          const previousValue =
            typeof toast.meta?.previousValue === "number"
              ? toast.meta.previousValue
              : undefined
          const currentValue =
            typeof toast.meta?.currentValue === "number"
              ? toast.meta.currentValue
              : undefined
          const theme = mode ? MODE_THEME[mode] : undefined
          const hasTransition =
            typeof previousValue === "number" && typeof currentValue === "number"
          const IconComponent = theme?.icon

          return (
            <Toast.Root
              width={{ md: "sm" }}
              borderWidth={theme ? "1px" : undefined}
              borderColor={theme?.border}
              bg={theme ? "black" : undefined}
              color={theme ? "whiteAlpha.900" : undefined}
              px={theme ? "3" : undefined}
              py={theme ? "2" : undefined}
              minH={theme ? "auto" : undefined}
              mx="auto"
              alignItems="center"
            >
              {toast.type === "loading" ? (
                <Spinner size="sm" color="blue.solid" />
              ) : IconComponent ? (
                <Icon as={IconComponent} color={theme?.color} boxSize="4" />
              ) : (
                <Toast.Indicator />
              )}
              <Stack
                gap="0.5"
                flex="1"
                maxWidth="100%"
                textAlign={theme ? "center" : undefined}
                alignItems={theme ? "center" : undefined}
              >
                {toast.title && (
                  <Toast.Title color={theme?.color}>{toast.title}</Toast.Title>
                )}
                {toast.description && (
                  <Toast.Description color={theme ? "whiteAlpha.800" : undefined}>
                    {toast.description}
                  </Toast.Description>
                )}
                {hasTransition ? (
                  <HStack
                    gap="2"
                    align="center"
                    justify="center"
                    color={theme?.color ?? "fg.muted"}
                  >
                    <Text fontSize="sm" opacity={0.75}>
                      {previousValue}
                    </Text>
                    <Text fontSize="sm" opacity={0.75}>
                      {"->"}
                    </Text>
                    <Text
                      key={`${mode}-${previousValue}-${currentValue}`}
                      fontSize="sm"
                      fontWeight="semibold"
                      lineHeight="1"
                    >
                      {currentValue}
                    </Text>
                  </HStack>
                ) : null}
              </Stack>
              {toast.action && (
                <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
              )}
              {toast.closable && <Toast.CloseTrigger />}
            </Toast.Root>
          )
        }}
      </ChakraToaster>
    </Portal>
  )
}
